<script setup>
/**
 * FormControl Factory
 * Dynamically resolves and renders form components based on table names,
 * column names, or field types.
 * * Note: Uses shallowRef to avoid Proxy-wrapping component definitions.
 */
import { ref, computed, onMounted, getCurrentInstance, shallowRef } from 'vue';
import camelCase from 'lodash/camelCase';

// --- Imports (Maintaining your existing list) ---
import FormControlCoursesDatesBookings from "../../bespoke/flyspain/FormControlCoursesDatesBookings";
import FormControlMRegistrantsRegistrantsCreditAmount from "../../bespoke/odp/FormControlMRegistrantsRegistrantsCreditAmount";
import FormControlOdpCourseBookingsAmountPaid from "../../bespoke/odp/FormControlOdpCourseBookingsAmountPaid";
import FormControlOdpCourseBookingsCourseDateId from "../../bespoke/odp/FormControlOdpCourseBookingsCourseDateId";
import FormControlOdpCourseDatesPromotionId from "../../bespoke/odp/FormControlOdpCourseDatesPromotionId";
import FormControlOdpCourseDatesCourseId from "../../bespoke/odp/FormControlOdpCourseDatesCourseId";
import FormControlOdpCourseDatesBookings from "../../bespoke/odp/FormControlOdpCourseDatesBookings";
import FormControlOdpCourseDatesEvent from "../../bespoke/odp/FormControlOdpCourseDatesEvent";
import FormControlOdpPartyBookingsResendEmail from "../../bespoke/odp/FormControlOdpPartyBookingsResendEmail";
import FormControlCategoriesComponentMenu from "../form-control/FormControlCategoriesComponentMenu.vue";
import FormControlActivityRowId from "../form-control/FormControlActivityRowId.vue";
import FormControlUsersAdministrator from "../form-control/FormControlUsersAdministrator";
import FormControlUsersRoles from "../form-control/FormControlUsersRoles";
import FormControlUsersGroupsRoles from "../form-control/user-groups-roles/FormControlUsersGroupsRoles";
import FormControlProductVariablesOptionId from "../form-control/FormControlProductVariablesOptionId";
import FormControlProductTypesButtons from "../form-control/FormControlProductTypesButtons";
import FormControlProductTypesFeatures from "../form-control/FormControlProductTypesFeatures";
import FormControlProductTypesShipping from "../form-control/FormControlProductTypesShipping";
import FormControlProductOptionsButtons from "../form-control/FormControlProductOptionsButtons";
import FormControlProductOptionsProductTypeId from "../form-control/FormControlProductOptionsProductTypeId";
import FormControlMProductsProductsTypeId from "../form-control/FormControlMProductsProductsTypeId";
import FormControlMProductsProductsCategoryId from "../form-control/FormControlMProductsProductsCategoryId";
import FormControlMProductsProductsTypeFeatures from "../form-control/FormControlMProductsProductsTypeFeatures";
import FormControlPagesComponentId from "../form-control/FormControlPagesComponentId";
import FormControlPagesDisplayInSitemap from "../form-control/FormControlPagesDisplayInSitemap";
import FormControlPagesNavigation from "../form-control/FormControlPagesNavigation";
import FormControlPagesTemplateId from "../form-control/FormControlPagesTemplateId";
import FormControlPagesType from "../form-control/FormControlPagesType";
import FormControlPagesUrl from "../form-control/FormControlPagesUrl";
import FormControlPagesUrlSlug from "../form-control/FormControlPagesUrlSlug";
import FormControlMRegistrantsRegistrantsLogin from "../form-control/FormControlMRegistrantsRegistrantsLogin";
import FormControlMRegistrantsRegistrantsSessionLogs from "../form-control/FormControlMRegistrantsRegistrantsSessionLogs";
import FormControlMRegistrantsRegistrantsUsername from "../form-control/FormControlMRegistrantsRegistrantsUsername";
import FormControlMBasketMerchantdetailsSettings from "../form-control/FormControlMBasketMerchantdetailsSettings";
import FormControlModulecomponentsDisplayName from "../form-control/FormControlModulecomponentsDisplayName";
import FormControlModulecomponentsNameField from "../form-control/FormControlModulecomponentsNameField.vue";
import FormControlModulecomponentsTitleColumn from "../form-control/FormControlModulecomponentsTitleColumn";
import FormControlModulecomponentsThumbnailColumn from "../form-control/FormControlModulecomponentsThumbnailColumn";
import FormControlModulecomponentsTableName from "../form-control/FormControlModulecomponentsTableName";
import FormControlModulecomponentsCategoryColumn from "../form-control/FormControlModulecomponentsCategoryColumn";
import FormControlModulecomponentsCategoryComponent from "../form-control/FormControlModulecomponentsCategoryComponent";
import FormControlAccountSettingsSecondaryDomains from "../form-control/FormControlAccountSettingsSecondaryDomains";
import FormControlAccountSettingsPrimaryDomain from "../form-control/FormControlAccountSettingsPrimaryDomain";
import FormControlAccountSettingsReorderScripts from "../form-control/FormControlAccountSettingsReorderScripts";
import FormControlContentPageUse from "../form-control/FormControlContentPageUse";
import FormControlContentClass from "../form-control/FormControlContentClass.vue";
import FormControlContentTitle from "../form-control/FormControlContentTitle";
import FormControlContentContentType from "../form-control/FormControlContentContentType";
import FormControlContentContentTargetContentArea from "../form-control/FormControlContentContentTargetContentArea";
import FormControlMBasketOrderitemsVariationId from "../form-control/FormControlMBasketOrderitemsVariationId";
import FormControlMBasketOrdersCustomerEmailMessageId from "../form-control/FormControlMBasketOrdersCustomerEmailMessageId";
import FormControlMBasketOrdersInternalEmailMessageId from "../form-control/FormControlMBasketOrdersInternalEmailMessageId";
import FormControlMBasketOrdersLogs from "../form-control/FormControlMBasketOrdersLogs";
import FormControlMBasketOrdersRequestAReview from "../form-control/FormControlMBasketOrdersRequestAReview";
import FormControlMBasketOrdersQuantity from "../form-control/FormControlMBasketOrdersQuantity";
import FormControlMBasketOrdersSessionLog from "../form-control/FormControlMBasketOrdersSessionLog";
import FormControlMProductsProductsShipping from "../form-control/FormControlMProductsProductsShipping";
import FormControlModulestructureBrowser from "../form-control/FormControlModulestructureBrowser";
import FormControlModulestructureCategoryComponentId from "../form-control/FormControlModulestructureCategoryComponentId";
import FormControlModulestructureColumnName from "../form-control/FormControlModulestructureColumnName";
import FormControlModulestructureDateFormat from "../form-control/FormControlModulestructureDateFormat";
import FormControlModulestructureDefaultValue from "../form-control/FormControlModulestructureDefaultValue";
import FormControlModulestructureEndYear from "../form-control/FormControlModulestructureEndYear";
import FormControlModulestructureStartYear from "../form-control/FormControlModulestructureStartYear";
import FormControlModulestructureNumRows from "../form-control/FormControlModulestructureNumRows";
import FormControlModulestructureOptions from "../form-control/FormControlModulestructureOptions";
import FormControlModulestructurePassword from "../form-control/FormControlModulestructurePassword";
import FormControlModulestructureSelectType from "../form-control/FormControlModulestructureSelectType";
import FormControlModulestructureType from "../form-control/FormControlModulestructureType";
import FormControlModulestructureTableTargetColumn from "../form-control/FormControlModulestructureTableTargetColumn";
import FormControlStockQuantity from "../form-control/FormControlStockQuantity";
import FormControlUrlSlug from "../form-control/FormControlUrlSlug";
import FormControlPortalSitesBuildName from "../form-control/FormControlPortalSitesBuildName";
import FormControlEmailMessageAttachments from "../form-control/FormControlEmailMessageAttachments";
import FormControlEmailMessageEvent from "../form-control/FormControlEmailMessageEvent";
import FormControlEmailMessageEventDate from "../form-control/FormControlEmailMessageEventDate";
import FormControlEmailMessageHtmlBody from "../form-control/FormControlEmailMessageHtmlBody";
import FormControlVariationsOption from "../form-control/FormControlVariationsOption";
import FormControlTagsTagUsage from '../form-control/form-control-tags-usage/FormControlTagsTagUsage';
import FormControlThemesCssGroups from '../form-control/FormControlThemesCssGroups';
import FormControlTemplatesTemplatesComponentId from '../form-control/FormControlTemplatesTemplatesComponentId';
import FormControlTemplatesTemplatesContentType from '../form-control/FormControlTemplatesTemplatesContentType';
import FormControlTemplatesTemplatesCssGroupName from '../form-control/FormControlTemplatesTemplatesCssGroupName';
import FormControlTemplatesTemplatesFilterAttribute from '../form-control/FormControlTemplatesTemplatesFilterAttribute';
import FormControlTemplatesTemplatesLazyLoad from '../form-control/FormControlTemplatesTemplatesLazyLoad';
import FormControlFilesFileName from '../form-control/FormControlFilesFileName';
import FormControlFilesFileSize from '../form-control/FormControlFilesFileSize';
import FormControlFilesUsage from '../form-control/FormControlFilesUsage';
import FormControlFranchiseId from '../form-control/FormControlFranchiseId';

// Type components
import FormControlTypeCheckbox from "../form-control-type/FormControlTypeCheckbox";
import FormControlTypeColor from "../form-control-type/FormControlTypeColor";
import FormControlTypeCodeEditor from "../form-control-type/FormControlTypeCodeEditor";
import FormControlTypeCollectionFilters from "../form-control-type/FormControlTypeCollectionFilters.vue";
import FormControlTypeCurrency from "../form-control-type/FormControlTypeCurrency";
import FormControlTypeDate from "../form-control-type/FormControlTypeDate";
import FormControlTypeDateDate from "../form-control-type/FormControlTypeDateDate";
import FormControlTypeDatetimeLocal from "../form-control-type/FormControlTypeDatetimeLocal";
import FormControlTypeEmail from "../form-control-type/FormControlTypeEmail";
import FormControlTypeFile from "../form-control-type/FormControlTypeFile";
import FormControlTypeImage from "../form-control-type/FormControlTypeImage";
import FormControlTypeInternalLink from "../form-control-type/FormControlTypeInternalLink";
import FormControlTypeJson from "../form-control-type/FormControlTypeJson";
import FormControlTypeMonth from "../form-control-type/FormControlTypeMonth";
import FormControlTypeNumber from "../form-control-type/FormControlTypeNumber";
import FormControlTypePassword from "../form-control-type/FormControlTypePassword";
import FormControlTypeRange from "../form-control-type/FormControlTypeRange";
import FormControlTypeRelationshipOneToMany from "../form-control-type/FormControlTypeRelationshipOneToMany";
import FormControlTypeRelationshipManyToMany from "../form-control-type/FormControlTypeRelationshipManyToMany";
import FormControlTypeSelect from "../form-control-type/FormControlTypeSelect";
import FormControlTypeSelectOther from "../form-control-type/FormControlTypeSelectOther";
import FormControlTypeTable from "../form-control-type/FormControlTypeTable";
import FormControlTypeTel from "../form-control-type/FormControlTypeTel";
import FormControlTypeText from "../form-control-type/FormControlTypeText";
import FormControlTypeTextarea from "../form-control-type/FormControlTypeTextarea";
import FormControlTypeTexteditor from "../form-control-type/FormControlTypeTexteditor";
import FormControlTypeTime from "../form-control-type/FormControlTypeTime";
import FormControlTypeUrl from "../form-control-type/FormControlTypeUrl";
import FormControlTypeWeek from "../form-control-type/FormControlTypeWeek";

/**
 * COMPONENT REGISTRY
 * Replaces the $options.components lookup.
 * Every component used must be present in this object.
 */
const COMPONENTS = {
    FormControlCoursesDatesBookings, FormControlMRegistrantsRegistrantsCreditAmount, FormControlOdpCourseBookingsAmountPaid,
    FormControlOdpCourseBookingsCourseDateId, FormControlOdpCourseDatesPromotionId, FormControlOdpCourseDatesCourseId,
    FormControlOdpCourseDatesBookings, FormControlOdpCourseDatesEvent, FormControlOdpPartyBookingsResendEmail,
    FormControlCategoriesComponentMenu, FormControlActivityRowId, FormControlUsersAdministrator, FormControlUsersRoles,
    FormControlUsersGroupsRoles, FormControlProductVariablesOptionId, FormControlProductTypesButtons, FormControlProductTypesFeatures,
    FormControlProductTypesShipping, FormControlProductOptionsButtons, FormControlProductOptionsProductTypeId, FormControlMProductsProductsTypeId,
    FormControlMProductsProductsCategoryId, FormControlMProductsProductsTypeFeatures, FormControlPagesComponentId,
    FormControlPagesDisplayInSitemap, FormControlPagesNavigation, FormControlPagesTemplateId,
    FormControlPagesType, FormControlPagesUrl, FormControlPagesUrlSlug, FormControlMRegistrantsRegistrantsLogin,
    FormControlMRegistrantsRegistrantsSessionLogs, FormControlMRegistrantsRegistrantsUsername, FormControlMBasketMerchantdetailsSettings,
    FormControlMBasketOrderitemsVariationId, FormControlMBasketOrdersCustomerEmailMessageId, FormControlMBasketOrdersInternalEmailMessageId,
    FormControlMBasketOrdersLogs, FormControlMBasketOrdersRequestAReview, FormControlMBasketOrdersQuantity,
    FormControlMBasketOrdersSessionLog, FormControlModulecomponentsDisplayName, FormControlModulecomponentsNameField,
    FormControlModulecomponentsTitleColumn, FormControlModulecomponentsThumbnailColumn, FormControlModulecomponentsTableName,
    FormControlModulecomponentsCategoryColumn, FormControlModulecomponentsCategoryComponent, FormControlAccountSettingsSecondaryDomains,
    FormControlAccountSettingsPrimaryDomain, FormControlAccountSettingsReorderScripts,
    FormControlContentPageUse, FormControlContentClass, FormControlContentTitle, FormControlContentContentType,
    FormControlContentContentTargetContentArea, FormControlMProductsProductsShipping, FormControlModulestructureBrowser,
    FormControlModulestructureCategoryComponentId, FormControlModulestructureColumnName, FormControlModulestructureDateFormat,
    FormControlModulestructureDefaultValue, FormControlModulestructureEndYear, FormControlModulestructureStartYear,
    FormControlModulestructureNumRows, FormControlModulestructureOptions, FormControlModulestructurePassword,
    FormControlModulestructureSelectType, FormControlModulestructureType, FormControlModulestructureTableTargetColumn,
    FormControlStockQuantity, FormControlUrlSlug, FormControlPortalSitesBuildName, FormControlEmailMessageAttachments,
    FormControlEmailMessageEvent, FormControlEmailMessageEventDate, FormControlEmailMessageHtmlBody, FormControlVariationsOption,
    FormControlTagsTagUsage, FormControlThemesCssGroups, FormControlTemplatesTemplatesComponentId, FormControlTemplatesTemplatesContentType,
    FormControlTemplatesTemplatesCssGroupName,
    FormControlTemplatesTemplatesFilterAttribute, FormControlTemplatesTemplatesLazyLoad, FormControlFilesFileName,
    FormControlFilesFileSize, FormControlFilesUsage, FormControlFranchiseId, FormControlTypeCheckbox,
    FormControlTypeColor, FormControlTypeCodeEditor, FormControlTypeCollectionFilters, FormControlTypeCurrency,
    FormControlTypeDate, FormControlTypeDateDate, FormControlTypeDatetimeLocal, FormControlTypeEmail,
    FormControlTypeFile, FormControlTypeImage, FormControlTypeInternalLink, FormControlTypeJson,
    FormControlTypeMonth, FormControlTypeNumber, FormControlTypePassword, FormControlTypeRange,
    FormControlTypeRelationshipOneToMany, FormControlTypeRelationshipManyToMany, FormControlTypeSelect,
    FormControlTypeSelectOther, FormControlTypeTable, FormControlTypeTel, FormControlTypeText,
    FormControlTypeTextarea, FormControlTypeTexteditor, FormControlTypeTime, FormControlTypeUrl, FormControlTypeWeek
};

const props = defineProps({
    formId: String,
    field: Object,
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// Use shallowRef to keep component definitions from being made reactive
const componentName = shallowRef(null);

// --- Computed ---

const currentData = computed(() => store.state[props.formId]?.currentData || {});
const component = computed(() => store.state.componentRoot.component);
const tableName = computed(() => store.state[props.formId]?.tableName);

// --- Logic ---

const setType = () => {
    if (props.field.type === 'select') {
        let selectType = parseInt(props.field.selectType);
        if (props.field.otherInput || selectType === 1) {
            props.field.type = 'select-other';
        } else if (selectType === 2) {
            props.field.type = 'switch-selects';
        }
    }
};

const init = async () => {
    // 1. Logic for cleaning up fields
    let toBeDeleted = [
        {tableName: 'users', fieldNames: ['assume']},
        {tableName: 'modulestructure', fieldNames: ['customItem']},
        {tableName: 'm_products_products', fieldNames: ['variations']},
    ];

    let removed = false;
    toBeDeleted.forEach((c) => {
        if (tableName.value === c.tableName && c.fieldNames.includes(props.field.columnName)) {
            props.field.hidden = true;
            removed = true;
        }
    });

    if (removed) return;

    setType();

    // 2. Normalization
    props.field.name = props.field.name || props.field.columnName;
    props.field.required = !!props.field.required;
    props.field.disabled = !!props.field.disabled;
    props.field.autofocus = !!props.field.autofocus;
    props.field.readonly = !!props.field.readonly;

    if (
        component.value?.allowFormAccess &&
        component.value?.navigationGroup === 'Forms' &&
        proxy.$route.name === 'form'
    ) {
        props.field.readonly = true;
    }

    // 3. Specially named components
    if (props.field.name === 'urlSlug' && tableName.value !== 'pages') {
        componentName.value = COMPONENTS.FormControlUrlSlug;
        return;
    } else if (props.field.name === 'stockQuantity') {
        componentName.value = COMPONENTS.FormControlStockQuantity;
        return;
    }

    // 4. Async content template lookup
    // todo The component this targets converts the rel field to a select menu.
    //      This could be replaced by a generic feature allowing rel fields to be presented as menus
    if (tableName.value === 'content' && props.field.name === 'componentId') {
        const template = await store.dispatch('itemData/get', {
            tableName: 'templates_templates',
            id: currentData.value.contentType
        });
        if (template?.contentType === 'Categories') {
            componentName.value = COMPONENTS.FormControlCategoriesComponentMenu;
            return;
        }
    }

    // 5. Column-based custom lookup
    if (props.field.name) {
        let lookupTable = tableName.value.toLowerCase().replace(/_/g, '-');
        let lookupColumn = props.field.name;

        if (lookupColumn === 'franchiseId' && lookupTable !== 'users') {
            componentName.value = COMPONENTS.FormControlFranchiseId;
            return;
        }

        if (tableName.value.indexOf('product_variations__') === 0) {
            lookupTable = 'variations';
            if (props.field.name.indexOf('option') === 0) lookupColumn = 'option';
        }

        let tag = 'form-control-' + lookupTable + '-' + lookupColumn;
        let camel = camelCase(tag);
        let vueName = camel.charAt(0).toUpperCase() + camel.slice(1);

        if (COMPONENTS[vueName]) {
            componentName.value = COMPONENTS[vueName];
            return;
        }
    }

    // 6. Type-based lookup (Fallback)
    if (['hidden', 'fieldset'].includes(props.field.type)) {
        props.field.hidden = true;
        return;
    }

    let typeName = 'form-control-type-' + props.field.type;
    let typeCamel = camelCase(typeName);
    let typeVueName = typeCamel.charAt(0).toUpperCase() + typeCamel.slice(1);

    if (COMPONENTS[typeVueName]) {
        componentName.value = COMPONENTS[typeVueName];
    } else {
        console.warn('form-control component not found', {
            'field.name': props.field.name,
            vueComponentName: typeVueName,
        });
    }
};

onMounted(() => {
    init();
});
</script>

<template>
    <component
        :is="componentName"
        v-if="componentName"
        :current-data="currentData"
        :field="field"
        :form-id="formId"
        :value="currentData[field.name]"
    />
</template>